import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
const BLANK_REVIEW = { author: "", rating: 5, text: "", date: "" };

export default function GoogleSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings")); }, []);
  if (!s) return null;

  const setReview = (i, patch) => setS({ ...s, google_reviews: s.google_reviews.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/admin/settings", { ...s, google_rating: Number(s.google_rating) || 0, google_review_count: Number(s.google_review_count) || 0 });
      setS(r.data);
      toast.success("Google reviews badge updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="google-settings">
      <div>
        <h2 className="font-serif text-2xl font-bold text-ink">Google Reviews Badge</h2>
        <p className="text-sm text-ink/60 mt-1">Copy your rating and a few real reviews from your Google Business Profile. Set rating to 0 to hide the badge.</p>
      </div>
      <div className="bg-white border border-ink/10 rounded-2xl p-6 grid sm:grid-cols-3 gap-4">
        <label className="text-xs text-ink/60">Google rating (0–5)
          <input type="number" step="0.1" min="0" max="5" value={s.google_rating} onChange={(e) => setS({ ...s, google_rating: e.target.value })} className={`${inputCls} mt-1`} data-testid="google-field-rating" />
        </label>
        <label className="text-xs text-ink/60">Number of Google reviews
          <input type="number" min="0" value={s.google_review_count} onChange={(e) => setS({ ...s, google_review_count: e.target.value })} className={`${inputCls} mt-1`} data-testid="google-field-count" />
        </label>
        <label className="text-xs text-ink/60">Google reviews link (g.page/… or maps URL)
          <input value={s.google_reviews_url} onChange={(e) => setS({ ...s, google_reviews_url: e.target.value })} placeholder="https://g.page/r/.../review" className={`${inputCls} mt-1`} data-testid="google-field-url" />
        </label>
      </div>
      <div className="space-y-3">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60">Featured Google reviews (shown on the site, max 6)</p>
        {s.google_reviews.map((r, i) => (
          <div key={i} className="bg-white border border-ink/10 rounded-2xl p-4 grid sm:grid-cols-12 gap-3" data-testid={`google-review-row-${i}`}>
            <input value={r.author} onChange={(e) => setReview(i, { author: e.target.value })} placeholder="Reviewer name" className={`${inputCls} sm:col-span-4`} data-testid={`google-review-author-${i}`} />
            <select value={r.rating} onChange={(e) => setReview(i, { rating: Number(e.target.value) })} className={`${inputCls} sm:col-span-2`} data-testid={`google-review-rating-${i}`}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
            </select>
            <input value={r.date} onChange={(e) => setReview(i, { date: e.target.value })} placeholder="e.g. Aug 2026" className={`${inputCls} sm:col-span-3`} data-testid={`google-review-date-${i}`} />
            <button type="button" onClick={() => setS({ ...s, google_reviews: s.google_reviews.filter((_, j) => j !== i) })} className="sm:col-span-3 flex items-center justify-center gap-1 text-xs text-ink/50 hover:text-red-600" data-testid={`google-review-remove-${i}`}><Trash2 size={14} /> Remove</button>
            <textarea rows={2} value={r.text} onChange={(e) => setReview(i, { text: e.target.value })} placeholder="Review text" className={`${inputCls} sm:col-span-12`} data-testid={`google-review-text-${i}`} />
          </div>
        ))}
        {s.google_reviews.length < 6 && (
          <button type="button" onClick={() => setS({ ...s, google_reviews: [...s.google_reviews, { ...BLANK_REVIEW }] })} className="flex items-center gap-1.5 text-sm font-semibold text-terra" data-testid="google-review-add"><Plus size={15} /> Add a review</button>
        )}
      </div>
      <button type="submit" disabled={busy} className="flex items-center gap-2 bg-ink text-sand font-semibold px-6 py-3 rounded-full hover:bg-terra transition-colors disabled:opacity-60" data-testid="google-settings-save"><Save size={15} /> {busy ? "Saving…" : "Save badge"}</button>
    </form>
  );
}
